import { useState, useEffect, useMemo } from 'react'
import { getDistincts } from '@services/payrollService'

/**
 * Hook para gerenciar dimensões filtradas baseadas em seleções atuais
 * Quando um filtro é selecionado, os outros filtros mostram apenas valores relacionados
 */
export function useFilteredDimensions(initialFilters = {}, monthYear = null) {
  const [allDimensions, setAllDimensions] = useState({
    grupos: [],
    cargos: [],
    fundos: [],
    regimes: [],
    grupos_evento: [],
    descricoes_evento: [],
    situacoes: [],
    secretarias: []
  })
  const [filteredDimensions, setFilteredDimensions] = useState({
    grupos: [],
    cargos: [],
    fundos: [],
    regimes: [],
    grupos_evento: [],
    descricoes_evento: [],
    situacoes: [],
    secretarias: []
  })
  const [loading, setLoading] = useState(true)

  // Carregar todas as dimensões iniciais
  useEffect(() => {
    const abort = new AbortController()
    let active = true

    async function loadAllDimensions() {
      try {
        setLoading(true)
        const ds = await getDistincts({ limit: 1000, use_cache: 1 }, abort.signal)
        if (!active) return

        const base = {
          grupos: (ds.grupos || []).sort((a, b) => a.localeCompare(b)),
          cargos: (ds.cargos || []).sort((a, b) => a.localeCompare(b)),
          fundos: (ds.fundos || []).sort((a, b) => a.localeCompare(b)),
          regimes: (ds.regimes || []).sort((a, b) => a.localeCompare(b)),
          grupos_evento: (ds.grupos_evento || []).sort((a, b) => a.localeCompare(b)),
          descricoes_evento: (ds.descricoes_evento || []).sort((a, b) => a.localeCompare(b)),
          situacoes: (ds.situacoes || []).sort((a, b) => a.localeCompare(b)),
          secretarias: ds.secretarias || [] // Secretarias já vem como objeto
        }

        setAllDimensions(base)
        setFilteredDimensions(base) // Inicialmente, todas as opções estão disponíveis
      } catch (e) {
        if (!abort.signal.aborted && active) {
          console.error('Erro ao carregar dimensões:', e)
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadAllDimensions()
    return () => {
      active = false
      abort.abort()
    }
  }, [])

  // Serializa filtros para comparação estável
  // Usa JSON.stringify diretamente nas dependências para comparar valores
  const filterKey = useMemo(() => {
    return JSON.stringify(initialFilters)
  }, [
    JSON.stringify(initialFilters.grupo || []),
    JSON.stringify(initialFilters.cargo || []),
    JSON.stringify(initialFilters.fundo || []),
    JSON.stringify(initialFilters.regime || []),
    JSON.stringify(initialFilters.grupo_evento || []),
    JSON.stringify(initialFilters.evento || []),
    JSON.stringify(initialFilters.situacao || []),
    JSON.stringify(initialFilters.secretaria || [])
  ])

  // Calcular dimensões filtradas baseadas nos filtros ativos
  useEffect(() => {
    const abort = new AbortController()
    let active = true

    async function calculateFilteredDimensions() {
      // Verifica se há filtros ativos
      const hasActiveFilters = Object.values(initialFilters).some(
        arr => Array.isArray(arr) && arr.length > 0
      )

      if (!hasActiveFilters) {
        // Sem filtros, mostra todas as opções
        setFilteredDimensions(prev => {
          const prevStr = JSON.stringify(prev)
          const allStr = JSON.stringify(allDimensions)
          return prevStr === allStr ? prev : allDimensions
        })
        return
      }

      try {
        // Prepara filtros para a API do endpoint distincts
        const apiFilters = {}
        Object.entries(initialFilters).forEach(([key, arr]) => {
          if (Array.isArray(arr) && arr.length > 0) {
            // Mapeia nomes de filtros para nomes da API
            // 'evento' mapeia para 'desc_evento', 'situacao' para 'tipo_b'
            let apiKey = key
            if (key === 'evento') apiKey = 'desc_evento'
            else if (key === 'situacao') apiKey = 'tipo_b'
            apiFilters[apiKey] = arr
          }
        })

        // Usa o endpoint /distincts/ com filtros para buscar valores únicos de forma eficiente
        // Este endpoint retorna apenas valores distintos, ao invés de eventos completos
        const distinctsResponse = await getDistincts({
          limit: 2000,
          use_cache: 0,  // Não usa cache estático quando há filtros
          ...apiFilters
        }, abort.signal)

        if (!active) return

        // Atualiza as dimensões filtradas com os valores retornados
        const newFiltered = {
          grupos: distinctsResponse.grupos || [],
          cargos: distinctsResponse.cargos || [],
          fundos: distinctsResponse.fundos || [],
          regimes: distinctsResponse.regimes || [],
          grupos_evento: distinctsResponse.grupos_evento || [],
          descricoes_evento: distinctsResponse.descricoes_evento || [],
          situacoes: distinctsResponse.situacoes || [],
          secretarias: distinctsResponse.secretarias || []
        }

        // Se algum filtro não retornou resultados, mantém todas as opções para evitar filtros vazios
        Object.keys(newFiltered).forEach(key => {
          if (newFiltered[key].length === 0 && allDimensions[key].length > 0) {
            newFiltered[key] = allDimensions[key]
          }
        })

        // Só atualiza se realmente mudou (comparação profunda via JSON)
        const newFilteredStr = JSON.stringify(newFiltered)
        setFilteredDimensions(prev => {
          const prevStr = JSON.stringify(prev)
          return prevStr !== newFilteredStr ? newFiltered : prev
        })
      } catch (e) {
        if (!abort.signal.aborted && active) {
          console.error('Erro ao calcular dimensões filtradas:', e)
          // Em caso de erro, mostra todas as opções
          setFilteredDimensions(allDimensions)
        }
      }
    }

    // Só calcula se já temos as dimensões carregadas
    if (Object.keys(allDimensions).length > 0 && Object.values(allDimensions).some(arr => arr.length > 0)) {
      // Debounce para evitar muitas chamadas
      const timeoutId = setTimeout(() => {
        calculateFilteredDimensions()
      }, 300)

      return () => {
        clearTimeout(timeoutId)
        active = false
        abort.abort()
      }
    }

    return () => {
      active = false
      abort.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  // Retorna as opções filtradas para cada dimensão
  // Se não há filtros ativos, retorna todas as opções
  // Se há filtros, retorna apenas as opções relacionadas
  const getFilteredOptions = useMemo(() => {
    const hasActiveFilters = Object.values(initialFilters).some(
      arr => Array.isArray(arr) && arr.length > 0
    )

    if (!hasActiveFilters) {
      return allDimensions
    }

    return filteredDimensions
  }, [
    JSON.stringify(initialFilters.grupo || []),
    JSON.stringify(initialFilters.cargo || []),
    JSON.stringify(initialFilters.fundo || []),
    JSON.stringify(initialFilters.regime || []),
    JSON.stringify(initialFilters.grupo_evento || []),
    JSON.stringify(initialFilters.evento || []),
    JSON.stringify(initialFilters.situacao || []),
    JSON.stringify(initialFilters.secretaria || []),
    JSON.stringify(allDimensions),
    JSON.stringify(filteredDimensions)
  ])

  return {
    dimensions: getFilteredOptions,
    allDimensions,
    loading
  }
}

